/** Kop, navigatie en inhoud. Werkt als zijmenu op groot scherm en als tabbalk op mobiel. */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { TALEN, type Taal } from '@mizen/i18n';
import { useApp, type Thema } from '../context/App.tsx';
import { Knop, Melding } from '../ontwerp/index.tsx';
import { Merk } from '../ontwerp/Merk.tsx';
import { Feedbackknop } from './Feedback.tsx';

/**
 * Het hoofdmenu. Elk item heeft twee namen: de volledige naam voor het zijmenu
 * op een groot scherm, en een korte naam voor de tabbalk op een telefoon. Daar
 * staan zeven knoppen naast elkaar en past "Klanten en leveranciers" niet meer
 * leesbaar. Beide staan in de opmaak; het scherm bepaalt met CSS welke er is,
 * zodat een schermlezer er altijd precies een voorleest.
 */
const MENU: {
  pad: string;
  sleutel:
    | 'nav.dashboard'
    | 'nav.verkoop'
    | 'nav.uren'
    | 'nav.inkoop'
    | 'nav.bank'
    | 'nav.relaties'
    | 'nav.rapporten'
    | 'nav.instellingen';
  kort:
    | 'nav.kort.dashboard'
    | 'nav.kort.verkoop'
    | 'nav.kort.uren'
    | 'nav.kort.inkoop'
    | 'nav.kort.bank'
    | 'nav.kort.relaties'
    | 'nav.kort.rapporten'
    | 'nav.kort.instellingen';
  teken: string;
}[] = [
  {
    pad: '/',
    sleutel: 'nav.dashboard',
    kort: 'nav.kort.dashboard',
    teken: '◧',
  },
  {
    pad: '/facturen',
    sleutel: 'nav.verkoop',
    kort: 'nav.kort.verkoop',
    teken: '↗',
  },
  {
    pad: '/uren',
    sleutel: 'nav.uren',
    kort: 'nav.kort.uren',
    teken: '◷',
  },
  {
    pad: '/inkoop',
    sleutel: 'nav.inkoop',
    kort: 'nav.kort.inkoop',
    teken: '↙',
  },
  { pad: '/bank', sleutel: 'nav.bank', kort: 'nav.kort.bank', teken: '≡' },
  {
    pad: '/relaties',
    sleutel: 'nav.relaties',
    kort: 'nav.kort.relaties',
    teken: '☺',
  },
  {
    pad: '/cijfers',
    sleutel: 'nav.rapporten',
    kort: 'nav.kort.rapporten',
    teken: '▦',
  },
  {
    pad: '/instellingen',
    sleutel: 'nav.instellingen',
    kort: 'nav.kort.instellingen',
    teken: '⚙',
  },
];

export function Schil({ children }: { children: ReactNode }) {
  const { t, taal, zetTaal, thema, zetThema, administratie, ik, kiesAdministratie, afmelden } = useApp();

  const alleAdministraties = (ik?.organisaties ?? []).flatMap((organisatie) =>
    organisatie.administraties.map((admin) => ({
      ...admin,
      organisatie: organisatie.naam,
    })),
  );

  return (
    <div className="app">
      <a className="overslaan" href="#inhoud">
        {t('nav.naarInhoud')}
      </a>

      {/* Op een test- of acceptatieomgeving hoort onmiskenbaar te zijn dat je
          niet in de echte administratie zit. De server bepaalt of deze balk er
          staat; de webapp verzint hem niet zelf. */}
      {ik?.omgeving?.label && (
        // Bewust geen role="status": dat is een live region voor meldingen die
        // komen en gaan. Deze balk staat er permanent en is aanvullende
        // informatie over de omgeving.
        <aside className="omgevingsbalk" aria-label={t('omgeving.test')}>
          <strong>{ik.omgeving.label}</strong>
          <span>{t('omgeving.testUitleg')}</span>
        </aside>
      )}

      <header className="kop">
        <NavLink to="/" className="kop__merk" aria-label={t('app.naam')}>
          <Merk hoogte={34} titel={t('app.naam')} />
        </NavLink>

        {/* Bij één administratie draagt de kop alleen het logo: de naam van je
            eigen bedrijf hoef je niet op elk scherm te lezen, en hij staat in
            Instellingen. Werk je in meerdere administraties, dan is de keuzelijst
            geen versiering maar bediening, en blijft hij staan. */}
        {alleAdministraties.length > 1 && (
          <label>
            <span className="alleen-schermlezer">{t('nav.administratieKiezen')}</span>
            <select
              className="veld__invoer"
              style={{ minHeight: '2.25rem', maxWidth: '16rem' }}
              value={administratie?.administratie.id ?? ''}
              onChange={(gebeurtenis) => kiesAdministratie(gebeurtenis.target.value)}
            >
              {alleAdministraties.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.naam}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="kop__rechts">
          <label className="kop__voorkeur">
            <span className="alleen-schermlezer">{t('instellingen.taal')}</span>
            <select
              className="veld__invoer"
              style={{ minHeight: '2.25rem', width: 'auto' }}
              value={taal}
              onChange={(gebeurtenis) => zetTaal(gebeurtenis.target.value as Taal)}
            >
              {TALEN.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.naam}
                </option>
              ))}
            </select>
          </label>

          <label className="kop__voorkeur">
            <span className="alleen-schermlezer">{t('instellingen.thema')}</span>
            <select
              className="veld__invoer"
              style={{ minHeight: '2.25rem', width: 'auto' }}
              value={thema}
              onChange={(gebeurtenis) => zetThema(gebeurtenis.target.value as Thema)}
            >
              <option value="systeem">{t('instellingen.themaSysteem')}</option>
              <option value="licht">{t('instellingen.themaLicht')}</option>
              <option value="donker">{t('instellingen.themaDonker')}</option>
            </select>
          </label>

          <Feedbackknop />

          <Knop soort="stil" klein onClick={() => void afmelden()}>
            {t('nav.afmelden')}
          </Knop>
        </div>
      </header>

      {ik?.gebruiker?.impersonatie && (
        <div style={{ padding: 'var(--ruimte-3) var(--ruimte-4)' }}>
          <Melding soort="let-op" titel="Ondersteuning kijkt mee">
            Een medewerker van de ondersteuning werkt op dit moment in jouw administratie. Alles wat er
            gebeurt wordt vastgelegd en de toegang stopt automatisch.
          </Melding>
        </div>
      )}

      <div className="romp">
        <nav className="menu" aria-label={t('nav.hoofdmenu')}>
          <ul className="menu__lijst">
            {MENU.map((item) => (
              <li key={item.pad}>
                <NavLink to={item.pad} end={item.pad === '/'} className="menu__link" aria-current={undefined}>
                  <span aria-hidden="true">{item.teken}</span>
                  <span className="menu__tekst">{t(item.sleutel)}</span>
                  <span className="menu__tekst menu__tekst--kort">{t(item.kort)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="inhoud" id="inhoud" tabIndex={-1}>
          {administratie?.administratie.status === 'alleen_lezen' && (
            <Melding soort="let-op" titel="Deze administratie staat op alleen lezen">
              Je kunt alles bekijken en exporteren, maar niets meer wijzigen.
            </Melding>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
